import { request } from "../../../services/api"
import type { BaseApiResponse, BasePagination } from "../../../types/api.ts"
import type {
  CustomerFilterOptions,
  CustomerFilters
} from "../redux/customers.types.ts"
import type {
  Customer,
  CustomerMutateData,
  CustomerNameMatch,
  CustomerSearchResult
} from "../types/customer.types.ts"
import type {
  CustomerNameMatchesParams,
  SearchCustomersParams
} from "./services.types.ts"

const BASE_URL = "/crm/customers"

const PATHS = {
  getCustomers: `${BASE_URL}/`,
  getFilterOptions: `${BASE_URL}/filter_options/`,
  getCustomerDetails: (id: string) => `${BASE_URL}/${id}/`,
  updateCustomer: (id: string) => `${BASE_URL}/${id}/`,
  deleteCustomer: (id: string) => `${BASE_URL}/${id}/`,
  resendInvite: (id: string) => `${BASE_URL}/${id}/resend_invite/`,
  invite: (id: string) => `${BASE_URL}/${id}/invite/`,
  reactivate: (id: string) => `${BASE_URL}/${id}/reactivate/`,
  suspendCustomer: (id: string) => `${BASE_URL}/${id}/suspend/`,
  searchCustomers: `/crm/leads/customer_autocomplete/`,
  customerNameMatches: `/crm/leads/customer-name-matches/`
}

export const reactivateCustomer = (id: string) =>
  request.api.post<BaseApiResponse<null>>(PATHS.reactivate(id))

export const suspendCustomer = (id: string) =>
  request.api.post<BaseApiResponse<null>>(PATHS.suspendCustomer(id))

export const getCustomersFilterOptions = () =>
  request.api.get<CustomerFilterOptions, BaseApiResponse<void>>(
    PATHS.getFilterOptions
  )

export const getCustomers = (params: CustomerFilters) =>
  request.api.get<BasePagination<Customer>, BaseApiResponse<void>>(
    PATHS.getCustomers,
    params
  )

export const getCustomerDetails = (id: string) =>
  request.api.get<Customer, BaseApiResponse<void>>(PATHS.getCustomerDetails(id))

export const createCustomer = (data: Omit<CustomerMutateData, "is_invite">) =>
  request.api.post<BaseApiResponse<Customer>>(PATHS.getCustomers, data)

export const updateCustomer = ({
  id,
  ...newInfo
}: CustomerMutateData & { id: string }) =>
  request.api.patch<Customer>(PATHS.updateCustomer(id), newInfo)

export const deleteCustomer = (id: string) =>
  request.api.delete<BaseApiResponse<null>>(PATHS.deleteCustomer(id))

export const resendInvite = (id: string) =>
  request.api.post<BaseApiResponse<null>>(PATHS.resendInvite(id))

export const invite = (id: string) =>
  request.api.post<BaseApiResponse<null>>(PATHS.invite(id))

export const searchCustomers = (params: SearchCustomersParams) =>
  request.api.get<
    CustomerSearchResult[] | BasePagination<CustomerSearchResult>,
    BaseApiResponse<void>
  >(PATHS.searchCustomers, params)

export const getCustomerNameMatches = (params: CustomerNameMatchesParams) =>
  request.api.get<CustomerNameMatch[], BaseApiResponse<void>>(
    PATHS.customerNameMatches,
    params
  )
